import {
  api,
  type Anordnung,
  type Auflage,
  type Befund,
  type Eckdaten,
  type Einheit,
  type Einsatzabschnitt,
  type LonLat,
  type Posten,
  type PostenEingabe,
  type Sperrstelle,
  type SperrstelleEingabe,
  type ZugParameter,
} from './api/client';
import { aktuell, ersetzt, type Schritt } from './undo';

// Umkehrbare Änderungen. Jede Funktion liefert einen Schritt für useUndo().tue(…).
// IDs immer über aktuell(…) auflösen: Wiederhergestelltes bekommt eine neue ID.

const neueId = (vorher: { id: string }[], nachher: { id: string }[]): string | null => {
  const alt = new Set(vorher.map((x) => x.id));
  return nachher.find((x) => !alt.has(x.id))?.id ?? null;
};

// ---------- Route ----------

export function routeAendern(
  pv: string,
  alt: { coords: LonLat[]; parameter: ZugParameter } | null,
  neu: { coords: LonLat[]; parameter: ZugParameter },
  text = 'Route geändert',
): Schritt {
  return {
    text,
    vor: () => api.routeSpeichern(pv, neu.coords, neu.parameter),
    zurueck: async () => {
      if (!alt) throw new Error('Die erste Route lässt sich nicht entfernen – bitte neu zeichnen.');
      await api.routeSpeichern(pv, alt.coords, alt.parameter);
    },
  };
}

export function befundBestaetigen(pv: string, b: Befund, aufheben: boolean): Schritt {
  const an = () => api.bestaetigen(pv, b.schluessel);
  const aus = () => api.bestaetigungAufheben(pv, b.schluessel);
  return {
    text: aufheben ? 'Kenntnisnahme zurückgenommen' : 'Hinweis zur Kenntnis genommen',
    vor: aufheben ? aus : an,
    zurueck: aufheben ? an : aus,
  };
}

// ---------- Posten ----------

function eingabe(p: Posten): PostenEingabe {
  return {
    einheit_id: p.einheit_id,
    einsatzabschnitt_id: p.einsatzabschnitt_id,
    personen: p.personen,
    auftrag: p.auftrag,
    material: p.material,
  };
}

function aufgeloest(d: PostenEingabe): PostenEingabe {
  return {
    ...d,
    einheit_id: d.einheit_id && aktuell(d.einheit_id),
    einsatzabschnitt_id: d.einsatzabschnitt_id && aktuell(d.einsatzabschnitt_id),
  };
}

async function postenWiederherstellen(p: Posten): Promise<void> {
  const r = await api.postenAnlegen({
    sperrstelle_id: aktuell(p.sperrstelle_id),
    ...aufgeloest(eingabe(p)),
  });
  ersetzt(p.id, r.id);
}

export function postenAendern(p: Posten, neu: PostenEingabe, text = 'Posten geändert'): Schritt {
  return {
    text,
    vor: () => api.postenAendern(aktuell(p.id), aufgeloest(neu)),
    zurueck: () => api.postenAendern(aktuell(p.id), aufgeloest(eingabe(p))),
  };
}

export function postenLoeschen(p: Posten): Schritt {
  return {
    text: 'Posten entfernt',
    vor: () => api.postenLoeschen(aktuell(p.id)),
    zurueck: () => postenWiederherstellen(p),
  };
}

export function postenAnlegen(sperrstelleId: string, d: PostenEingabe): Schritt {
  let id: string | null = null;
  return {
    text: 'Posten hinzugefügt',
    vor: async () => {
      const r = await api.postenAnlegen({
        sperrstelle_id: aktuell(sperrstelleId),
        ...aufgeloest(d),
      });
      if (id) ersetzt(id, r.id);
      id ??= r.id;
    },
    zurueck: () => api.postenLoeschen(aktuell(id!)),
  };
}

export function vorschlagUebernehmen(pv: string, sperrstelleId: string): Schritt {
  let id: string | null = null;
  return {
    text: 'Vorschlag übernommen',
    vor: async () => {
      const vorher = (await api.posten(pv)).posten;
      const r = await api.vorschlagUebernehmen(pv, aktuell(sperrstelleId));
      const nid = neueId(vorher, r.posten);
      if (nid && id) ersetzt(id, nid);
      id ??= nid;
    },
    zurueck: () => api.postenLoeschen(aktuell(id!)),
  };
}

// ---------- Einheiten und Abschnitte ----------

export function einheitAnlegen(d: Omit<Einheit, 'id'>): Schritt {
  let id: string | null = null;
  return {
    text: `Einheit „${d.name}“ angelegt`,
    vor: async () => {
      const vorher = (await api.einheiten()).einheiten;
      const nid = neueId(vorher, (await api.einheitAnlegen(d)).einheiten);
      if (nid && id) ersetzt(id, nid);
      id ??= nid;
    },
    zurueck: () => api.einheitLoeschen(aktuell(id!)),
  };
}

export function abschnittAnlegen(pv: string, d: Omit<Einsatzabschnitt, 'id'>): Schritt {
  let id: string | null = null;
  return {
    text: `Abschnitt „${d.name}“ angelegt`,
    vor: async () => {
      const vorher = (await api.abschnitte(pv)).einsatzabschnitte;
      const nid = neueId(vorher, (await api.abschnittAnlegen(pv, d)).einsatzabschnitte);
      if (nid && id) ersetzt(id, nid);
      id ??= nid;
    },
    zurueck: () => api.abschnittLoeschen(aktuell(id!)),
  };
}

/** Beim Wiederherstellen werden auch die Posten wieder dem Abschnitt zugeordnet. */
export function abschnittLoeschen(pv: string, a: Einsatzabschnitt, posten: Posten[]): Schritt {
  const betroffen = posten.filter((p) => p.einsatzabschnitt_id === a.id);
  return {
    text: `Abschnitt „${a.name}“ entfernt`,
    vor: () => api.abschnittLoeschen(aktuell(a.id)),
    zurueck: async () => {
      const vorher = (await api.abschnitte(pv)).einsatzabschnitte;
      const daten = {
        name: a.name,
        leiter: a.leiter,
        funkrufname: a.funkrufname,
        gespraechsgruppe: a.gespraechsgruppe,
      };
      const nid = neueId(vorher, (await api.abschnittAnlegen(pv, daten)).einsatzabschnitte);
      if (!nid) return;
      ersetzt(a.id, nid);
      for (const p of betroffen)
        await api.postenAendern(
          aktuell(p.id),
          aufgeloest({ ...eingabe(p), einsatzabschnitt_id: nid }),
        );
    },
  };
}

// ---------- Vorgabe (nur solange nicht freigegeben – sonst lehnt der Server ab) ----------

function sperrstelleEingabe(s: Sperrstelle): SperrstelleEingabe {
  return {
    kennung: s.kennung,
    bezeichnung: s.bezeichnung,
    massnahme: s.massnahme ?? 'vollsperrung',
    sperrzeit_von: s.sperrzeit_von,
    sperrzeit_bis: s.sperrzeit_bis,
    aufheben_nach_durchzug: s.aufheben_nach_durchzug,
    fundstelle: s.fundstelle ?? '',
    position: s.position,
  };
}

export function sperrstelleAnlegen(aid: string, d: SperrstelleEingabe): Schritt {
  let id: string | null = null;
  return {
    text: `Sperrstelle ${d.kennung ?? ''} erfasst`,
    vor: async () => {
      const r = await api.sperrstelleAnlegen(aid, d);
      if (id) ersetzt(id, r.id);
      id ??= r.id;
    },
    zurueck: () => api.sperrstelleLoeschen(aktuell(id!)),
  };
}

export function sperrstelleAendern(s: Sperrstelle, d: SperrstelleEingabe): Schritt {
  return {
    text: `Sperrstelle ${s.kennung ?? ''} korrigiert`,
    vor: () => api.sperrstelleAendern(aktuell(s.id), d),
    zurueck: () => api.sperrstelleAendern(aktuell(s.id), sperrstelleEingabe(s)),
  };
}

/** Löschen entfernt auch die Posten an der Stelle – beim Rückgängig kommen sie wieder. */
export function sperrstelleLoeschen(aid: string, s: Sperrstelle, posten: Posten[]): Schritt {
  const betroffen = posten.filter((p) => p.sperrstelle_id === s.id);
  return {
    text: `Sperrstelle ${s.kennung ?? ''} gelöscht`,
    vor: () => api.sperrstelleLoeschen(aktuell(s.id)),
    zurueck: async () => {
      const r = await api.sperrstelleAnlegen(aid, sperrstelleEingabe(s));
      ersetzt(s.id, r.id);
      for (const p of betroffen) await postenWiederherstellen(p);
    },
  };
}

export function anordnungAendern(
  a: Anordnung,
  d: { behoerde: string | null; aktenzeichen: string | null; datum: string | null },
): Schritt {
  const alt = { behoerde: a.behoerde, aktenzeichen: a.aktenzeichen, datum: a.datum };
  return {
    text: 'Angaben zur Anordnung geändert',
    vor: () => api.anordnungAendern(a.id, d),
    zurueck: () => api.anordnungAendern(a.id, alt),
  };
}

function auflageDaten(x: Auflage): Record<string, unknown> {
  return {
    text: x.text,
    fundstelle: x.fundstelle,
    pruefart: x.pruefart,
    regel_key: x.regel_key,
    ende_spaetestens: x.regel_key === 'ENDE_SPAETESTENS' ? (x.parameter?.zeit ?? null) : null,
    verantwortlich: x.verantwortlich,
  };
}

export function auflageAnlegen(vid: string, aid: string, d: Record<string, unknown>): Schritt {
  let id: string | null = null;
  return {
    text: 'Auflage hinzugefügt',
    vor: async () => {
      const vorher = (await api.anordnung(vid)).auflagen;
      const nid = neueId(vorher, (await api.auflageAnlegen(aid, d)).auflagen);
      if (nid && id) ersetzt(id, nid);
      id ??= nid;
    },
    zurueck: () => api.auflageLoeschen(aktuell(id!)),
  };
}

export function auflageErledigt(x: Auflage, erledigt: boolean): Schritt {
  return {
    text: erledigt ? 'Auflage abgehakt' : 'Auflage wieder offen',
    vor: () => api.auflageAendern(aktuell(x.id), { erledigt }),
    zurueck: () => api.auflageAendern(aktuell(x.id), { erledigt: !erledigt }),
  };
}

export function auflageLoeschen(aid: string, x: Auflage, alle: Auflage[]): Schritt {
  return {
    text: 'Auflage gelöscht',
    vor: () => api.auflageLoeschen(aktuell(x.id)),
    zurueck: async () => {
      const nid = neueId(
        alle.filter((y) => y.id !== x.id),
        (await api.auflageAnlegen(aid, auflageDaten(x))).auflagen,
      );
      if (!nid) return;
      ersetzt(x.id, nid);
      if (x.erledigt) await api.auflageAendern(nid, { erledigt: true });
    },
  };
}

// ---------- Eckdaten und Einstellungen ----------

export function eckdatenAendern(id: string, alt: Eckdaten, neu: Eckdaten): Schritt {
  return {
    text: 'Eckdaten geändert',
    vor: () => api.veranstaltungAendern(id, neu),
    zurueck: () => api.veranstaltungAendern(id, alt),
  };
}

export function ressourcenSetzen(
  alt: Record<string, number>,
  neu: Record<string, number>,
): Schritt {
  return {
    text: 'Materialbestand geändert',
    vor: () => api.ressourcenSetzen(neu),
    zurueck: () => api.ressourcenSetzen(alt),
  };
}

/** Regelwerk ist versioniert: Rückgängig speichert den alten Inhalt als neue Version. */
export function regelwerkSpeichern(alt: unknown, neu: unknown): Schritt {
  return {
    text: 'Regelwerk geändert',
    vor: () => api.regelwerkSpeichern(neu),
    zurueck: () => api.regelwerkSpeichern(alt),
  };
}
